// ─── Part Assembly Service ───
// Migrated from Supabase direct INSERT to Alibaba Next Job API.
//
// Worker: gbcospack-part-assembly (PM2) — claims pending/queued jobs,
//         runs Kling Omni compose, stores result in part-assembly-results bucket.
//
// Image A = base container (base_image_id | base_image_url)
// Image B = part to attach  (part_image_id | part_image_url)
//
// ⛔ Supabase from('part_assembly_jobs').insert(...) 제거
// ⛔ Supabase from('part_assembly_jobs').select(...) 제거 (poll 역시 Job API GET 사용)
// ⛔ finishing / printing 필드 혼용 금지 (structured_lines, part_code, finish_code, color_code)
// ⛔ 클라이언트에서 final_kling_prompt 조립 금지
// ──────────────────────────────────────────────────
import { supabase } from '@/lib/supabase';

// ─── Types (Job API) ───

export interface PartAssemblyJobApi {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  progress: {
    step: string | null;
    message: string | null;
    percent: number;
  };
  error: {
    code: string | null;
    message: string | null;
  } | null;
  result: {
    result_id: string | null;
    image_url: string | null;
    storage_bucket: string | null;
    storage_path: string | null;
  } | null;
  client_request_id: string | null;
}

export interface CreateJobResponse {
  ok: boolean;
  job: PartAssemblyJobApi;
  request_id: string;
}

export interface GetJobResponse {
  ok: boolean;
  job: PartAssemblyJobApi;
  request_id: string;
}

// ─── Service Types (backward-compatible consumed by ProcessSection) ───

export interface PartAssemblyJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress_percent: number;
  progress_message: string;
  result_id: string | null;
  result_image_url: string | null;
  error_message: string | null;
}

export interface PartAssemblyResult {
  id: string;
  public_url: string | null;
}

export interface CreatePartAssemblyJobRequest {
  product_id: string;
  base_image_url?: string;
  part_image_url?: string;
  base_image_id?: string;
  part_image_id?: string;
  source?: string;
  request_locale?: string;
}

// ─── requireApiBaseUrl (absolute only — no relative fallback) ───

function requireApiBaseUrl(): string {
  const base = String(import.meta.env.VITE_API_BASE_URL ?? '')
    .trim()
    .replace(/\/$/, '');
  if (!base) {
    throw new Error(
      'Missing VITE_API_BASE_URL. Part-assembly Job API requires an absolute GBCOSPACK API base URL (no relative /api fallback).',
    );
  }
  if (!/^https?:\/\//i.test(base)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute http(s) URL (got "${base}"). Do not use relative /api paths.`,
    );
  }
  return base;
}

// ─── Map API job → service type ───

function mapApiJobToService(job: PartAssemblyJobApi): PartAssemblyJob {
  return {
    id: job.id,
    status: job.status,
    progress_percent: job.progress?.percent ?? 0,
    progress_message: job.progress?.message || '',
    result_id: job.result?.result_id || null,
    result_image_url: job.result?.image_url || null,
    error_message: job.error?.message || null,
  };
}

// ─── Create Part Assembly Job (Job API POST) ───

export async function createPartAssemblyJob(
  request: CreatePartAssemblyJobRequest,
): Promise<PartAssemblyJob> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('로그인이 필요합니다.');
  }

  const apiBase = requireApiBaseUrl();
  const jobId = crypto.randomUUID();
  const clientRequestId = `ui-assembly-${jobId}`;

  const body: Record<string, unknown> = {
    id: jobId,
    client_request_id: clientRequestId,
    product_id: request.product_id,
    source: request.source || 'readdy_customer_ui',
    request_locale: request.request_locale || 'ko-KR',
  };

  // Image A — base container (UUID preferred, URL fallback)
  if (request.base_image_id) {
    body.base_image_id = request.base_image_id;
  }
  if (request.part_image_id) {
    body.part_image_id = request.part_image_id;
  }
  if (request.base_image_url) {
    body.base_image_url = request.base_image_url;
  }
  if (request.part_image_url) {
    body.part_image_url = request.part_image_url;
  }

  console.log('[PartAssembly] Creating job via Job API:', {
    product_id: request.product_id,
    has_base_image_id: !!request.base_image_id,
    has_part_image_id: !!request.part_image_id,
    has_base_image_url: !!request.base_image_url,
    has_part_image_url: !!request.part_image_url,
    clientRequestId,
  });

  const res = await fetch(`${apiBase}/api/v1/jobs/part-assembly`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const responseText = await res.text().catch(() => '');
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(responseText);
  } catch {
    console.error('[PartAssembly] Non-JSON create response:', responseText.slice(0, 500));
    throw new Error(
      `API 서버에서 예상치 못한 응답을 반환했습니다 (HTTP ${res.status}). 잠시 후 다시 시도해주세요.`,
    );
  }

  if (!res.ok || !json.ok) {
    const errMsg = json?.error?.message ?? `Job create failed (${res.status})`;
    console.error('[PartAssembly] Job API create error:', {
      httpStatus: res.status,
      error: errMsg,
    });
    throw new Error(errMsg);
  }

  console.log('[PartAssembly] Job created via API:', {
    jobId: json.job.id,
    status: json.job.status,
    requestId: json.request_id,
  });

  return mapApiJobToService(json.job as PartAssemblyJobApi);
}

// ─── Poll Part Assembly Job (Job API GET) ───

export async function pollPartAssemblyJobStatus(jobId: string): Promise<PartAssemblyJob> {
  const { data: { session } } = await supabase.auth.getSession();
  const apiBase = requireApiBaseUrl();

  const res = await fetch(`${apiBase}/api/v1/jobs/part-assembly/${jobId}`, {
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
    },
  });

  const responseText = await res.text().catch(() => '');
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(responseText);
  } catch {
    console.error('[PartAssembly] Non-JSON poll response:', responseText.slice(0, 500));
    throw new Error(
      `API 서버에서 예상치 못한 응답을 반환했습니다 (HTTP ${res.status}). 잠시 후 다시 시도해주세요.`,
    );
  }

  if (!res.ok || !json.ok) {
    const errMsg = json?.error?.message ?? `Poll failed (${res.status})`;
    throw new Error(errMsg);
  }

  return mapApiJobToService(json.job as PartAssemblyJobApi);
}

// ─── Get Part Assembly Result ───
// Results are stored by the worker in the part-assembly-results bucket.
// Since the Job API GET response already includes result.image_url (signed URL),
// this function is retained for backward compatibility but new code should prefer
// the result_image_url from pollPartAssemblyJobStatus().

export async function getPartAssemblyResult(_resultId: string): Promise<PartAssemblyResult> {
  // NOTE: In the Job API flow, result.image_url is already provided by pollPartAssemblyJobStatus().
  // This function exists only for backward compatibility.
  // result_id from the API job can be used to construct the result reference.
  return {
    id: _resultId,
    public_url: null, // caller should use result_image_url from pollPartAssemblyJobStatus()
  };
}